import { retryDelayMs } from "@/hooks/studyMetadataRetry";

/**
 * One retry rule for the reader-session requests (ENG-229): the saved-decisions
 * and saved-annotations loads and the decision save.
 *
 * Retry when sending the same request again could give a different answer: no
 * response (network drop or timeout), 5xx, 408 or 429. Any other 4xx is the
 * server's final answer about this request (including a 409 conflict), so it
 * fails at once.
 */
export function isFinalRequestError(error: unknown): boolean {
  const status = responseStatus(error);
  return status != null && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

export function isTransientRequestError(error: unknown): boolean {
  return !isFinalRequestError(error);
}

/** Retries after the first try, so up to four attempts in total. */
export const MAX_REQUEST_RETRIES = 3;

/** Backoff before retry `n` (1-based): about 0.6 / 1.2 / 2.4 s. Tests may set it to 0. */
export const requestRetryTiming = { delayMs: retryDelayMs };

/** TanStack Query `retry` / `retryDelay` options following this rule. */
export const transientQueryRetry = {
  retry: (failureCount: number, error: Error) =>
    failureCount < MAX_REQUEST_RETRIES && isTransientRequestError(error),
  retryDelay: (failureCount: number) => requestRetryTiming.delayMs(failureCount + 1),
};

const SAVE_TIMEOUT_SCHEDULE_MS = [15_000, 20_000, 25_000, 30_000];
const SAVE_TIMEOUT_CEILING_MS = 45_000;

/**
 * Timeout for save attempt `attempt` (1-based). A good connection gets the
 * 15 / 20 / 25 / 30 s schedule. A connection whose saves already took longer
 * gets 1.5x its slowest successful save, so a limit is never set lower than
 * this connection can manage. Never above 45 s.
 */
export function saveAttemptTimeoutMs(attempt: number, slowestSuccessMs: number): number {
  const scheduled =
    SAVE_TIMEOUT_SCHEDULE_MS[Math.min(attempt, SAVE_TIMEOUT_SCHEDULE_MS.length) - 1];
  return Math.min(SAVE_TIMEOUT_CEILING_MS, Math.max(scheduled, Math.ceil(slowestSuccessMs * 1.5)));
}

export function isTimeoutError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return responseStatus(error) == null && (code === "ECONNABORTED" || code === "ETIMEDOUT");
}

/** The HTTP status of an axios-style error, read by shape like isRetryableMetadataError. */
function responseStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === "number" ? status : undefined;
}
